import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
  FaChevronDown,
  FaSearch,
  FaPaperPlane,
  FaTimes,
  FaInbox,
  FaRegClock,
  FaTicketAlt,
  FaImage,
  FaPaperclip,
} from "react-icons/fa";
import { useToast } from "../context/ToastContext";
import {
  ROLE,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  MAX_ATTACHMENTS,
  fileToAttachment,
  getCurrentIdentity,
  getInbox,
  getMyRequests,
  getTicket,
  createTicket,
  replyToTicket,
  setTicketStatus,
} from "../api/services/supportTicketsService";
import "../styles/Support.css";

/* ----------------------------------------------------------------------
   Support contact details — update these to your real support channels.
---------------------------------------------------------------------- */
const SUPPORT_EMAIL = "support@mindscraft.pk";
const SUPPORT_PHONE = "+92 300 1234567";
const SUPPORT_WHATSAPP = "+92 300 1234567";

const FAQS = [
  {
    q: "How do I create a new administrator user?",
    a: "Go to Users from the sidebar and click the Create User button in the top-right. Fill in the administrator's details, assign a role and permissions, then save.",
  },
  {
    q: "How do I onboard a new organization?",
    a: "Open Organizations from the sidebar and use the onboarding action. Provide the organization profile, contact and industry details, then submit to register it.",
  },
  {
    q: "Why can't I see certain menu items in the sidebar?",
    a: "The menu adapts to your account type and assigned roles. Super Admins see all modules; organization admins and subordinate users only see the sections their permissions allow.",
  },
  {
    q: "How do I export reports and transaction data?",
    a: "Most list pages include an export action that downloads a CSV or PDF. Use the filters first to narrow the data, then trigger the export.",
  },
];

/* ---------- small helpers ---------- */
const roleLabel = (role) =>
  ({
    [ROLE.SUPER_ADMIN]: "Super Admin",
    [ROLE.ORG_ADMIN]: "Organization Admin",
    [ROLE.SUBORDINATE]: "User",
    [ROLE.MOBILE_USER]: "App User",
  }[role] || "User");

const statusClass = (s) =>
  ({
    Open: "is-open",
    "In Progress": "is-progress",
    Resolved: "is-resolved",
    Closed: "is-closed",
  }[s] || "is-open");

const priorityClass = (p) =>
  ({
    Low: "is-low",
    Medium: "is-medium",
    High: "is-high",
    Urgent: "is-urgent",
  }[p] || "is-medium");

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const StatusBadge = ({ status }) => (
  <span className={`sup-badge ${statusClass(status)}`}>{status}</span>
);
const PriorityBadge = ({ priority }) => (
  <span className={`sup-badge sup-badge--pri ${priorityClass(priority)}`}>
    {priority}
  </span>
);

/* ---------- image attachments ---------- */
const ImagePicker = ({ attachments, onChange, onError, busy, setBusy }) => {
  const inputRef = useRef(null);
  const atLimit = attachments.length >= MAX_ATTACHMENTS;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file
    if (!files.length) return;

    const room = MAX_ATTACHMENTS - attachments.length;
    if (files.length > room) {
      onError(`You can attach up to ${MAX_ATTACHMENTS} images.`);
    }

    setBusy(true);
    const accepted = [];
    for (const file of files.slice(0, room)) {
      try {
        accepted.push(await fileToAttachment(file));
      } catch (err) {
        onError(err.message);
      }
    }
    setBusy(false);
    if (accepted.length) onChange([...attachments, ...accepted]);
  };

  return (
    <div className="sup-attach">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleFiles}
      />
      <button
        type="button"
        className="sup-attachbtn"
        onClick={() => inputRef.current?.click()}
        disabled={atLimit || busy}
        title={atLimit ? `Maximum ${MAX_ATTACHMENTS} images` : "Attach an image"}
      >
        <FaPaperclip />
        {busy ? "Processing…" : atLimit ? "Limit reached" : "Attach image"}
      </button>

      {attachments.length > 0 && (
        <div className="sup-thumbs">
          {attachments.map((a) => (
            <div key={a.id} className="sup-thumb">
              <img src={a.url ?? a.dataUrl} alt={a.name} />
              <button
                type="button"
                className="sup-thumb__remove"
                onClick={() => onChange(attachments.filter((x) => x.id !== a.id))}
                aria-label={`Remove ${a.name}`}
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Images inside a conversation message */
const MessageImages = ({ attachments, onOpen }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="sup-msg__imgs">
      {attachments.map((a, idx) => (
        <button
          key={a.attachmentId || a.id || idx}
          type="button"
          className="sup-msg__img"
          onClick={() => onOpen(a)}
          title={a.name}
        >
          <img src={a.url ?? a.dataUrl} alt={a.name} />
        </button>
      ))}
    </div>
  );
};

/* ====================================================================== */
export default function Support() {
  const { showSuccess, showError } = useToast();
  const identity = useMemo(() => getCurrentIdentity(), []);

  const isSuperAdmin = identity.role === ROLE.SUPER_ADMIN;
  const isOrgAdmin = identity.role === ROLE.ORG_ADMIN;
  const canCreate = !isSuperAdmin; // super admin only receives
  const hasInbox = isSuperAdmin || isOrgAdmin;
  const recipientLabel = isOrgAdmin ? "Super Admin" : "Organization Admin";

  const [tab, setTab] = useState(
    isSuperAdmin ? "inbox" : isOrgAdmin ? "inbox" : "mine"
  );

  const [inbox, setInbox] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selected, setSelected] = useState(null); // { ticket, mode }
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyBusy, setReplyBusy] = useState(false);
  const [savingAction, setSavingAction] = useState(false);

  const [form, setForm] = useState({
    category: TICKET_CATEGORIES[0],
    priority: "Medium",
    subject: "",
    message: "",
  });
  const [formFiles, setFormFiles] = useState([]);
  const [formFilesBusy, setFormFilesBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [lightbox, setLightbox] = useState(null); // attachment being viewed full-size

  const [faqQuery, setFaqQuery] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, m] = await Promise.all([
        hasInbox ? getInbox(identity) : Promise.resolve([]),
        canCreate ? getMyRequests(identity) : Promise.resolve([]),
      ]);
      setInbox(i);
      setMine(m);
    } catch (e) {
      showError("Load failed", "Could not load support tickets.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInbox, canCreate]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- create ---- */
  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.subject.trim()) next.subject = "Please enter a subject.";
    if (!form.message.trim()) next.message = "Please describe your issue.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await createTicket(identity, {
        subject: form.subject.trim(),
        category: form.category,
        priority: form.priority,
        message: form.message.trim(),
        attachments: formFiles,
      });
      showSuccess("Ticket submitted", `Your request was sent to the ${recipientLabel}.`);
      setForm({ category: TICKET_CATEGORIES[0], priority: "Medium", subject: "", message: "" });
      setFormFiles([]);
      await load();
      if (!isSuperAdmin) setTab(isOrgAdmin ? "requests" : "mine");
    } catch (err) {
      showError("Could not submit", err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---- reply / status ---- */
  const openTicket = async (ticket, mode) => {
    // Show immediately with the list row data, then pull the full conversation
    // (list endpoints don't include the messages array).
    setSelected({ ticket, mode });
    setReplyText("");
    setReplyFiles([]);
    try {
      const full = await getTicket(ticket.id);
      setSelected((prev) =>
        prev && prev.ticket.id === ticket.id ? { ...prev, ticket: full } : prev
      );
    } catch (err) {
      // keep the list data; surface why the conversation didn't load
      showError(
        "Could not open ticket",
        err?.message || "You may not have access to this ticket."
      );
    }
  };
  const closeTicket = () => {
    setSelected(null);
    setReplyText("");
    setReplyFiles([]);
  };

  const refreshSelected = (updated) => {
    setSelected((prev) => (prev ? { ...prev, ticket: updated } : prev));
  };

  const handleReply = async () => {
    if (!selected) return;
    // a reply is valid with just an image, or just text
    if (!replyText.trim() && replyFiles.length === 0) return;
    setSavingAction(true);
    try {
      const updated = await replyToTicket(
        identity,
        selected.ticket.id,
        replyText.trim(),
        replyFiles
      );
      refreshSelected(updated);
      setReplyText("");
      setReplyFiles([]);
      await load();
    } catch (err) {
      showError("Reply failed", err.message || "Could not post your reply.");
    } finally {
      setSavingAction(false);
    }
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    setSavingAction(true);
    try {
      const updated = await setTicketStatus(selected.ticket.id, status);
      refreshSelected(updated);
      await load();
      showSuccess("Status updated", `Ticket marked "${status}".`);
    } catch (err) {
      showError("Update failed", "Could not change the status.");
    } finally {
      setSavingAction(false);
    }
  };

  /* ---- filtering ---- */
  const applyFilters = (list) => {
    const q = search.trim().toLowerCase();
    return list.filter((t) => {
      const okStatus = statusFilter === "all" || t.status === statusFilter;
      const okSearch =
        !q ||
        t.subject.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q) ||
        (t.createdBy?.name || "").toLowerCase().includes(q);
      return okStatus && okSearch;
    });
  };

  const inboxCounts = useMemo(() => {
    const c = { Open: 0, "In Progress": 0, Resolved: 0, Closed: 0 };
    inbox.forEach((t) => {
      c[t.status] = (c[t.status] || 0) + 1;
    });
    return c;
  }, [inbox]);

  const filteredFaqs = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    );
  }, [faqQuery]);

  /* ---- header subtitle per role ---- */
  const subtitle = isSuperAdmin
    ? "Review and resolve support tickets escalated by organization admins."
    : isOrgAdmin
    ? "Handle tickets from your users, and raise issues to the Super Admin."
    : "Raise a support request and track its status.";

  /* ---- render pieces ---- */
  const renderTicketList = (list, mode) => {
    const rows = applyFilters(list);
    if (loading) {
      return <div className="sup-empty">Loading tickets…</div>;
    }
    if (rows.length === 0) {
      return (
        <div className="sup-empty">
          <FaInbox style={{ fontSize: 22, opacity: 0.5 }} />
          <div>No tickets to show.</div>
        </div>
      );
    }
    return (
      <div className="sup-tlist">
        {rows.map((t) => {
          const imageCount = (t.messages || []).reduce(
            (n, m) => n + (m.attachments?.length || 0),
            0
          );
          return (
          <button
            key={t.id}
            type="button"
            className="sup-trow"
            onClick={() => openTicket(t, mode)}
          >
            <div className="sup-trow__main">
              <div className="sup-trow__subject">{t.subject}</div>
              <div className="sup-trow__meta">
                <span className="sup-trow__id">{t.id}</span>
                <span className="sup-dot">•</span>
                <span>{t.createdBy?.name}</span>
                <span className="sup-dot">•</span>
                <span>{t.category}</span>
                {imageCount > 0 && (
                  <>
                    <span className="sup-dot">•</span>
                    <span className="sup-trow__imgs" title={`${imageCount} image(s) attached`}>
                      <FaImage /> {imageCount}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="sup-trow__side">
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
              <span className="sup-trow__time">
                <FaRegClock /> {timeAgo(t.updatedAt)}
              </span>
            </div>
          </button>
          );
        })}
      </div>
    );
  };

  const renderToolbar = () => (
    <div className="sup-toolbar">
      <div className="sup-search">
        <FaSearch className="sup-search__icon" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by subject, ID or sender…"
        />
      </div>
      <div className="sup-pillrow">
        {["all", ...TICKET_STATUSES].map((s) => (
          <button
            key={s}
            type="button"
            className={`sup-pill ${statusFilter === s ? "active" : ""}`}
            onClick={() => setStatusFilter(s)}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>
    </div>
  );

  const renderStats = () => (
    <div className="sup-stats">
      <div className="sup-stat">
        <div className="sup-stat__value">{inbox.length}</div>
        <div className="sup-stat__label">Total</div>
      </div>
      <div className="sup-stat">
        <div className="sup-stat__value is-open">{inboxCounts.Open}</div>
        <div className="sup-stat__label">Open</div>
      </div>
      <div className="sup-stat">
        <div className="sup-stat__value is-progress">{inboxCounts["In Progress"]}</div>
        <div className="sup-stat__label">In Progress</div>
      </div>
      <div className="sup-stat">
        <div className="sup-stat__value is-resolved">{inboxCounts.Resolved}</div>
        <div className="sup-stat__label">Resolved</div>
      </div>
    </div>
  );

  const renderCreateForm = () => (
    <form className="sup-form" onSubmit={handleCreate} noValidate>
      <div className="sup-form__note">
        <FaTicketAlt /> This request will be sent to the <b>{recipientLabel}</b>.
      </div>
      <div className="sup-row">
        <div className="sup-field">
          <label className="sup-label">Category</label>
          <select
            className="sup-select"
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
          >
            {TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="sup-field">
          <label className="sup-label">Priority</label>
          <select
            className="sup-select"
            value={form.priority}
            onChange={(e) => setField("priority", e.target.value)}
          >
            {TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="sup-field">
        <label className="sup-label">
          Subject<span className="sup-req">*</span>
        </label>
        <input
          className="sup-input"
          type="text"
          value={form.subject}
          onChange={(e) => setField("subject", e.target.value)}
          placeholder="Brief summary of your issue"
        />
        {errors.subject && <span className="sup-error">{errors.subject}</span>}
      </div>
      <div className="sup-field">
        <label className="sup-label">
          Message<span className="sup-req">*</span>
        </label>
        <textarea
          className="sup-textarea"
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          placeholder="Describe the problem, what you expected, and steps to reproduce."
        />
        {errors.message && <span className="sup-error">{errors.message}</span>}
      </div>
      <div className="sup-field">
        <label className="sup-label">
          Screenshots <span className="sup-label__hint">(optional, up to {MAX_ATTACHMENTS})</span>
        </label>
        <ImagePicker
          attachments={formFiles}
          onChange={setFormFiles}
          onError={(m) => showError("Attachment", m)}
          busy={formFilesBusy}
          setBusy={setFormFilesBusy}
        />
      </div>
      <button className="sup-submit" type="submit" disabled={submitting || formFilesBusy}>
        <FaPaperPlane />
        {submitting ? "Submitting…" : "Submit ticket"}
      </button>
    </form>
  );

  return (
    <div className="sup-page">
      <div className="sup-header">
        <h2 className="sup-title">Support Center</h2>
        <div className="sup-subtitle">{subtitle}</div>
      </div>

      {/* Role-aware working area */}
      {isSuperAdmin && (
        <div className="sup-card">
          <div className="sup-card__head sup-card__head--row">
            <div>
              <h3 className="sup-card__title">Incoming tickets</h3>
              <div className="sup-card__sub">Escalated by organization admins.</div>
            </div>
          </div>
          <div className="sup-card__body">
            {renderStats()}
            {renderToolbar()}
            {renderTicketList(inbox, "inbox")}
          </div>
        </div>
      )}

      {isOrgAdmin && (
        <div className="sup-card">
          <div className="sup-tabs">
            <button
              type="button"
              className={`sup-tab ${tab === "inbox" ? "active" : ""}`}
              onClick={() => setTab("inbox")}
            >
              <FaInbox /> Incoming from users
              {inbox.length > 0 && <span className="sup-tab__count">{inbox.length}</span>}
            </button>
            <button
              type="button"
              className={`sup-tab ${tab === "requests" ? "active" : ""}`}
              onClick={() => setTab("requests")}
            >
              <FaTicketAlt /> My requests to Super Admin
            </button>
          </div>
          <div className="sup-card__body">
            {tab === "inbox" ? (
              <>
                {renderStats()}
                {renderToolbar()}
                {renderTicketList(inbox, "inbox")}
              </>
            ) : (
              <div className="sup-grid">
                <div>
                  <div className="sup-subhead">Raise a new request</div>
                  {renderCreateForm()}
                </div>
                <div>
                  <div className="sup-subhead">Your submitted requests</div>
                  {renderTicketList(mine, "mine")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!isSuperAdmin && !isOrgAdmin && (
        <div className="sup-card">
          <div className="sup-card__body">
            <div className="sup-grid">
              <div>
                <div className="sup-subhead">Raise a new request</div>
                {renderCreateForm()}
              </div>
              <div>
                <div className="sup-subhead">Your submitted requests</div>
                {renderTicketList(mine, "mine")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shared help resources — hidden for Super Admin (they only manage the inbox) */}
      {!isSuperAdmin && (
        <>
      <div className="sup-channels">
        <a className="sup-channel" href={`mailto:${SUPPORT_EMAIL}`}>
          <div className="sup-channel__icon"><FaEnvelope /></div>
          <div>
            <div className="sup-channel__label">Email us</div>
            <div className="sup-channel__value">{SUPPORT_EMAIL}</div>
          </div>
        </a>
        <a className="sup-channel" href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}>
          <div className="sup-channel__icon"><FaPhoneAlt /></div>
          <div>
            <div className="sup-channel__label">Call us</div>
            <div className="sup-channel__value">{SUPPORT_PHONE}</div>
          </div>
        </a>
        <a
          className="sup-channel"
          href={`https://wa.me/${SUPPORT_WHATSAPP.replace(/[^\d]/g, "")}`}
          target="_blank"
          rel="noreferrer"
        >
          <div className="sup-channel__icon"><FaWhatsapp /></div>
          <div>
            <div className="sup-channel__label">WhatsApp</div>
            <div className="sup-channel__value">{SUPPORT_WHATSAPP}</div>
          </div>
        </a>
      </div>

      <div className="sup-card">
        <div className="sup-card__head">
          <h3 className="sup-card__title">Frequently asked questions</h3>
        </div>
        <div className="sup-card__body">
          <div className="sup-faq-search">
            <FaSearch className="sup-faq-search__icon" />
            <input
              type="text"
              value={faqQuery}
              onChange={(e) => setFaqQuery(e.target.value)}
              placeholder="Search help articles…"
            />
          </div>
          <div className="sup-faq-list">
            {filteredFaqs.length === 0 && (
              <div className="sup-faq-empty">No articles match “{faqQuery}”.</div>
            )}
            {filteredFaqs.map((f) => {
              const realIndex = FAQS.indexOf(f);
              const isOpen = openFaq === realIndex;
              return (
                <div key={f.q} className={`sup-faq-item ${isOpen ? "open" : ""}`}>
                  <button
                    type="button"
                    className="sup-faq-q"
                    onClick={() => setOpenFaq(isOpen ? -1 : realIndex)}
                    aria-expanded={isOpen}
                  >
                    <span>{f.q}</span>
                    <FaChevronDown className="sup-faq-q__chev" />
                  </button>
                  {isOpen && <div className="sup-faq-a">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
        </>
      )}

      {/* Ticket detail modal */}
      {selected && (
        <div className="sup-modal" role="dialog" aria-modal="true">
          <div className="sup-modal__scrim" onClick={closeTicket} />
          <div className="sup-modal__panel">
            <div className="sup-modal__head">
              <div>
                <div className="sup-modal__id">{selected.ticket.id}</div>
                <h3 className="sup-modal__title">{selected.ticket.subject}</h3>
                <div className="sup-modal__badges">
                  <PriorityBadge priority={selected.ticket.priority} />
                  <StatusBadge status={selected.ticket.status} />
                  <span className="sup-modal__cat">{selected.ticket.category}</span>
                </div>
              </div>
              <button type="button" className="sup-modal__close" onClick={closeTicket} aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="sup-modal__meta">
              From <b>{selected.ticket.createdBy?.name}</b>{" "}
              <span className="sup-modal__role">{roleLabel(selected.ticket.createdBy?.role)}</span>
            </div>

            <div className="sup-thread">
              {(selected.ticket.messages || []).map((m, idx) => (
                <div
                  key={idx}
                  className={`sup-msg ${m.role === identity.role ? "mine" : ""}`}
                >
                  <div className="sup-msg__head">
                    <span className="sup-msg__author">{m.author}</span>
                    <span className="sup-msg__role">{roleLabel(m.role)}</span>
                    <span className="sup-msg__time">{timeAgo(m.at)}</span>
                  </div>
                  {m.text && <div className="sup-msg__text">{m.text}</div>}
                  <MessageImages attachments={m.attachments} onOpen={setLightbox} />
                </div>
              ))}
            </div>

            <div className="sup-modal__foot">
              <textarea
                className="sup-textarea"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                rows={3}
              />
              <ImagePicker
                attachments={replyFiles}
                onChange={setReplyFiles}
                onError={(m) => showError("Attachment", m)}
                busy={replyBusy}
                setBusy={setReplyBusy}
              />
              <div className="sup-modal__actions">
                {selected.mode === "inbox" && (
                  <select
                    className="sup-select sup-statusselect"
                    value={selected.ticket.status}
                    onChange={(e) => handleStatus(e.target.value)}
                    disabled={savingAction}
                    title="Change status"
                  >
                    {TICKET_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className="sup-submit"
                  onClick={handleReply}
                  disabled={
                    savingAction ||
                    replyBusy ||
                    (!replyText.trim() && replyFiles.length === 0)
                  }
                >
                  <FaPaperPlane />
                  {savingAction ? "Sending…" : "Send reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-size image viewer */}
      {lightbox && (
        <div
          className="sup-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="sup-lightbox__close"
            onClick={() => setLightbox(null)}
            aria-label="Close image"
          >
            <FaTimes />
          </button>
          <img
            src={lightbox.url ?? lightbox.dataUrl}
            alt={lightbox.name}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="sup-lightbox__name">{lightbox.name}</div>
        </div>
      )}
    </div>
  );
}
